import { trackCampaignPage } from '~/utils/adTracking';
import { h, render } from 'preact';
import Episodes from './../../components/Episodes/Episode';
import Player from '../../components/Player/Player';
import { mostRecentStore } from '../../mostRecentStore';
import MostRecent from '../../components/MostRecent/MostRecent';
import Clipboard from 'clipboard';
import PopularPodcasts from '../../components/PopularPodcasts';
import { getProgress, player as s } from '../../player/store';
import { shouldAutoplayEpisode } from './autoplay';
import Notification from '~/notification';
import { commitOptimisticLike } from '~/pages/Podcast/optimisticLike';
import {
    clearPendingEpisodeLike,
    fetchEpisodeLikeStatus,
    getPendingEpisodeLike,
    isPersistedEpisodeId,
    likeEpisode,
    setPendingEpisodeLike,
    unlikeEpisode,
} from '~/episodeLikesAccount';
import { loadPublicAuthState } from '~/publicAuth';
import type { Episode as PodcastEpisode, Podcast } from '~/types/podcast';

declare global {
    interface Window {
        __INITIAL_STATE:
            | {
                  notfound: true;
                  podcast: never;
                  podcastUrl: never;
                  episode?: never;
                  episodeUrl?: never;
                  auth?: never;
              }
            | {
                  notfound?: undefined;
                  podcast: Podcast;
                  podcastUrl: string;
                  episode: PodcastEpisode & { id: number };
                  episodeUrl: string;
                  auth: {
                      loginUrl: string;
                  };
              };
    }
}

function Episode(/** match */) {
    /* Player in overlay view / BIG */
    window.addEventListener(
        'player:big.enter',
        () => {
            if (!document.documentElement.classList.contains('player--is-big')) {
                document.documentElement.className += ' player--is-big';
            }
        },
        false,
    );
    window.addEventListener(
        'player:big.leave',
        () => {
            if (document.documentElement.classList.contains('player--is-big')) {
                document.documentElement.classList.remove('player--is-big');
            }
        },
        false,
    );
    const shareButton = document.querySelector('.podcast-share');
    const shareBoard = document.querySelector('#share_pod');
    const closeShareButton = shareBoard?.querySelector('.close-share');
    const clipboard = new Clipboard('#copy_url');
    clipboard.on('success', () => {
        const copyButton = shareBoard?.querySelector<HTMLSpanElement>('#copy_url > span');
        const rawText = copyButton?.innerText;
        if (!copyButton || !rawText) {
            return;
        }

        copyButton.innerText = 'Kopierad!';
        setTimeout(() => {
            shareBoard?.classList.add('hidden');
            copyButton.innerText = rawText;
        }, 400);
    });

    closeShareButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        shareBoard?.classList.add('hidden');
    });

    shareButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        shareBoard?.classList.remove('hidden');
    });

    document.addEventListener('click', (e) => {
        if (shareBoard?.classList.contains('hidden')) {
            return;
        }

        if (!shareBoard?.contains(e.target as Node)) {
            shareBoard?.classList.add('hidden');
        }
    });

    if (typeof window.__INITIAL_STATE === 'undefined') {
        window.__INITIAL_STATE = {};
    }

    if (window.__INITIAL_STATE.notfound) return;

    const podcast = window.__INITIAL_STATE.podcast;
    const episode = window.__INITIAL_STATE.episode;
    let episodeId = isPersistedEpisodeId(episode.id) ? episode.id : null;
    const autoplay = shouldAutoplayEpisode(window.location.search);
    const loginUrl = window.__INITIAL_STATE.auth.loginUrl;

    const likeButton = document.querySelector('.js-episode-like');
    const [animatedHeart, baseHeart] = likeButton?.querySelectorAll('svg') ?? [];
    const notification = new Notification(document.querySelector('.js-liked-episode-toast'), {
        hideAfter: 8000,
    });
    const errorNotification = new Notification(
        document.querySelector('.js-episode-like-error-toast'),
        {
            hideAfter: 8000,
        },
    );

    let isAuthenticated = false;
    let accountLikesEnabled = false;
    let isLiked = false;
    let likeRequestInFlight = false;
    let viewerStateLoaded = false;
    let viewerStatePromise: Promise<void> | null = null;

    const setLikeState = (liked: boolean, { animate = false } = {}) => {
        isLiked = liked;

        if (!baseHeart || !animatedHeart) {
            return;
        }

        baseHeart.classList.toggle('fill-primary', liked);
        baseHeart.classList.toggle('fill-current', liked);
        baseHeart.classList.toggle('text-brand-900', liked);
        baseHeart.classList.toggle('text-brand-700', !liked);
        likeButton?.classList.toggle('bg-brand-100', liked);
        likeButton?.classList.toggle('bg-slate-100', !liked);
        likeButton?.setAttribute('aria-pressed', String(liked));
        const label = liked
            ? 'Ta bort avsnittet från Mina sidor'
            : 'Spara avsnittet till Mina sidor';
        likeButton?.setAttribute('aria-label', label);
        likeButton?.setAttribute('title', label);

        if (!liked) {
            animatedHeart.classList.remove('animate-ping-once');
            animatedHeart.classList.add('hidden');
            return;
        }

        animatedHeart.classList.remove('hidden');
        if (animate) {
            animatedHeart.classList.remove('animate-ping-once');
            void animatedHeart.getBoundingClientRect();
            animatedHeart.classList.add('animate-ping-once');
        }
    };

    const syncPendingEpisodeLike = async () => {
        if (!isAuthenticated) {
            return;
        }

        const pendingEpisodeLike = getPendingEpisodeLike();
        if (!pendingEpisodeLike) {
            return;
        }

        await likeEpisode(pendingEpisodeLike);
        if (pendingEpisodeLike === episode.id) {
            setLikeState(true, { animate: true });
            notification.show();
        }
        clearPendingEpisodeLike();
    };

    const loadViewerState = async () => {
        if (viewerStateLoaded) {
            return;
        }

        if (!viewerStatePromise) {
            viewerStatePromise = (async () => {
                const authState = await loadPublicAuthState();

                isAuthenticated = authState.authenticated;
                accountLikesEnabled = authState.features.accountLikes;

                if (!episodeId || !accountLikesEnabled || !isAuthenticated) {
                    setLikeState(false);
                    return;
                }

                try {
                    await syncPendingEpisodeLike();
                } catch (error) {
                    console.warn('Pending episode like sync failed:', error);
                }

                try {
                    const liked = await fetchEpisodeLikeStatus(episodeId);
                    setLikeState(liked);
                } catch (error) {
                    console.warn('Episode like status check failed:', error);
                }
            })()
                .then(() => {
                    viewerStateLoaded = true;
                })
                .finally(() => {
                    viewerStatePromise = null;
                });
        }

        await viewerStatePromise;
    };

    const redirectToLogin = () => {
        const targetUrl = new URL(loginUrl, window.location.origin);
        targetUrl.searchParams.set('intended', window.location.pathname + window.location.search);

        window.location.assign(targetUrl.toString());
    };

    setLikeState(false);
    void loadViewerState();

    likeButton?.addEventListener('click', async (e) => {
        e.preventDefault();

        if (likeRequestInFlight) {
            return;
        }

        await loadViewerState();

        if (!accountLikesEnabled || !isAuthenticated) {
            if (episodeId) {
                setPendingEpisodeLike(episodeId);
            }

            redirectToLogin();
            return;
        }

        if (!episodeId) {
            if (!episode.media) {
                errorNotification.show();
                return;
            }

            likeRequestInFlight = true;
            setLikeState(true, { animate: true });

            try {
                const savedEpisodeId = await likeEpisode({
                    itunes_id: podcast.itunes_id,
                    media: episode.media,
                });

                if (savedEpisodeId) {
                    episodeId = savedEpisodeId;
                }

                errorNotification.hide();
                notification.show();
            } catch (error) {
                console.warn('Episode like failed:', error);
                setLikeState(false);
                notification.hide();
                errorNotification.show();
            } finally {
                likeRequestInFlight = false;
            }

            return;
        }

        likeRequestInFlight = true;

        try {
            await commitOptimisticLike({
                isLiked,
                setLikeState,
                like: () => likeEpisode(episodeId),
                unlike: () => unlikeEpisode(episodeId),
                onLiked: () => {
                    errorNotification.hide();
                    notification.show();
                },
                onError: () => {
                    notification.hide();
                    errorNotification.show();
                },
            });
        } catch {
            // The optimistic state has already been rolled back.
        } finally {
            likeRequestInFlight = false;
        }
    });

    mostRecentStore.load().trackVisit(podcast.itunes_id);

    const listenButton = document.querySelector('button.js-podcast_listen');
    if (listenButton) {
        listenButton.addEventListener('click', (e) => {
            e.preventDefault();
            if (window.__INITIAL_STATE.episode && !s.data.playing.value) {
                s.play(episode, podcast, getProgress(episode, podcast.itunes_id).currentSeek);
                e.preventDefault();
            }
        });
    }

    const mountPlayerNode = document.getElementById('playerplace');
    if (mountPlayerNode) {
        const currentSeek = getProgress(episode, podcast.itunes_id).currentSeek;

        s.mount(episode, podcast, currentSeek);
        render(h(Player, { store: s }), mountPlayerNode);

        if (autoplay && !s.data.playing.value) {
            s.play(episode, podcast, currentSeek);
        }
    }

    const episodesNode = document.querySelector('.episodes');
    if (episodesNode && episodesNode.parentElement) {
        episodesNode.innerHTML = '';
        render(
            h(Episodes, {
                store: s,
                podcast,
                title: podcast.title,
                header: 'Fler avsnitt av',
                podcastUrl: window.__INITIAL_STATE.podcastUrl,
            }),
            episodesNode.parentElement,
        );
    }
    const popularPodcastsNode = document.querySelector('#popularpodcast');
    if (popularPodcastsNode) {
        render(
            h(PopularPodcasts, {
                title: 'Populärt på Poddtoppen just nu',
                slot: 'popular.horizontal.episode',
                action: '/populara',
            }),
            popularPodcastsNode,
        );
    }

    const mostRecentNode = document.querySelector('#mostrecent');
    if (mostRecentNode) {
        render(
            h(MostRecent, {
                title: 'Senast besökta',
            }),
            mostRecentNode,
        );
    }
}

Episode();

if (window.__INITIAL_STATE && !window.__INITIAL_STATE.notfound) {
    trackCampaignPage(
        Number(window.__INITIAL_STATE.podcast.itunes_id),
        window.__INITIAL_STATE.episode.id,
    );
}
